import { useRef, useState } from 'react'

export type CheckboxDropdownItem = {
  id: string
  name: string
}

type Props = {
  label: string
  items: CheckboxDropdownItem[]
  onSelected: (ids: string[]) => void
  initialSelectedIds?: string[]
  showAllLabel?: string
}

export default function CheckboxDropdown({
  label,
  items,
  onSelected,
  initialSelectedIds = [],
  showAllLabel = 'Show all',
}: Props) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<string[]>(initialSelectedIds)
  const [showAllActive, setShowAllActive] = useState(false)
  const isClosingRef = useRef(false)

  const toggle = (id: string) => {
    if (showAllActive) return
    setSelectedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id]
      onSelected(next)
      return next
    })
  }

  const toggleShowAll = () => {
    if (showAllActive) {
      setShowAllActive(false)
    } else {
      setShowAllActive(true)
      setSelectedIds([])
      onSelected([])
    }
  }

  const displayLabel = showAllActive
    ? showAllLabel
    : selectedIds.length === 0
    ? label
    : selectedIds.length === 1
    ? items.find((i) => i.id === selectedIds[0])?.name ?? label
    : `${items.find((i) => i.id === selectedIds[0])?.name} (+${
        selectedIds.length - 1
      })`

  const renderItem = (item: CheckboxDropdownItem) => (
    <li
      key={item.id}
      className={`mentem-dropdown-button bg-white p-[6px] text-left ${
        showAllActive
          ? 'cursor-not-allowed'
          : 'cursor-pointer hover:bg-[#FFDC0026]'
      }`}
      onClick={(e) => {
        e.preventDefault()
        e.stopPropagation()
        toggle(item.id)
      }}
    >
      <label
        className={`flex items-center gap-2 px-2 leading-normal ${
          showAllActive ? 'cursor-not-allowed' : 'cursor-pointer'
        }`}
      >
        <input
          type="checkbox"
          aria-label={item.name}
          checked={selectedIds.includes(item.id)}
          disabled={showAllActive}
          onChange={() => toggle(item.id)}
          onClick={(e) => e.stopPropagation()}
          style={showAllActive ? { accentColor: '#A3A3A9' } : undefined}
        />
        {item.name}
      </label>
    </li>
  )

  return (
    <div
      className="dropdown relative"
      style={{ display: 'grid', justifyItems: 'center' }}
      onMouseEnter={() => {
        if (!isClosingRef.current) setIsOpen(true)
      }}
      onMouseLeave={() => {
        isClosingRef.current = true
        setIsOpen(false)
        setTimeout(() => {
          isClosingRef.current = false
        }, 200)
      }}
    >
      <div className="dropdown-box flex min-h-[36px] w-full min-w-[188px] items-center justify-between border border-primaryGrey px-3">
        <label className="mentem-dropdown-button flex-1 whitespace-normal text-center cursor-pointer">
          {displayLabel}
        </label>
        <svg
          width="14"
          height="9"
          viewBox="0 0 14 9"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          style={{ minWidth: 14 }}
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M0.292893 0.511643C0.653377 0.151159 1.22061 0.12343 1.6129 0.428455L1.70711 0.511643L7 5.80375L12.2929 0.511643C12.6534 0.151159 13.2206 0.12343 13.6129 0.428455L13.7071 0.511643C14.0676 0.872127 14.0953 1.43936 13.7903 1.83165L13.7071 1.92586L7.70711 7.92586C7.34662 8.28634 6.77939 8.31407 6.3871 8.00905L6.29289 7.92586L0.292893 1.92586C-0.0976311 1.53533 -0.0976311 0.902168 0.292893 0.511643Z"
            fill="#51597B"
          />
        </svg>
      </div>
      <div style={{ display: 'grid', justifyItems: 'center' }}>
        <ul
          className="mentem-scrollbar-2 dropdown-content bg-base-100 max-h-[60vh] w-full overflow-y-auto shadow"
          style={
            isOpen
              ? { opacity: 1, visibility: 'visible' }
              : { opacity: 0, visibility: 'hidden', pointerEvents: 'none' }
          }
        >
          {items.map(renderItem)}
          <li
            className="mentem-dropdown-button cursor-pointer bg-white p-[6px] text-left hover:bg-[#FFDC0026]"
            onClick={(e) => {
              e.preventDefault()
              e.stopPropagation()
              toggleShowAll()
            }}
          >
            <label className="flex cursor-pointer items-center gap-2 px-2">
              <input
                type="checkbox"
                aria-label={showAllLabel}
                checked={showAllActive}
                onChange={toggleShowAll}
                onClick={(e) => e.stopPropagation()}
              />
              {showAllLabel}
            </label>
          </li>
        </ul>
      </div>
    </div>
  )
}
